import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { UserRoles } from 'src/app/auth/current-user';
import { handleApiError } from 'src/app/helper/helper';
import { User } from 'src/app/interfaces/user.interface';
import { ApiService } from 'src/app/services/api.service';

@Component({
  standalone: false,
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.css'],
})
export class UserDetailComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  private id: number;
  user: User | undefined;
  userForm: FormGroup;
  roles: { id: number; name: string }[] = [
    { id: UserRoles.none, name: 'none' },
    { id: UserRoles.admin, name: 'admin' },
    { id: UserRoles.user, name: 'user' },
  ];
  validationError: string | undefined;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.id = -1;
    this.userForm = this.fb.group({
      name: ['', Validators.required],
      password: [''],
      password2: [''],
      playerId: [''],
      selectedRoles: [[] as UserRoles[]],
      notes: [''],
    });
    this.subscription = this.route.paramMap.subscribe((params) => {
      this.id = Number.parseInt(params.get('id') || '-1');
      this.loadUser();
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async loadUser() {
    if (this.id < 0) {
      return;
    }
    try {
      this.user = await this.apiService.fetchUser(this.id);
      this.userForm.patchValue({
        name: this.user.name,
        password: '',
        password2: '',
        playerId: '' + this.user.playerId,
        selectedRoles: this.user.roles,
        notes: this.user.note,
      });
    } catch (error) {
      handleApiError(this.authService, this.router, error);
    }
  }

  async onSubmit(): Promise<void> {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }

    if (this.userForm.value.password !== this.userForm.value.password2) {
      this.validationError = 'Passwörter stimmen nicht überein.';
      return;
    }

    const { name, password, playerId, selectedRoles, notes } = this.userForm.value;
    const updateData: any = {
      name,
      playerId: Number.parseInt(playerId || '0'),
      roles: selectedRoles,
      note: notes
    };
    if (password) {
      updateData.password = password;
    }

    try {
      await this.apiService.postUpdateUser(this.id, updateData);
      this.router.navigate(['/admin/user/list']);
    } catch (error) {
      console.error('Error updating user', error);
      this.validationError = 'Fehler beim Speichern des Benutzers.';
    }
  }

  async onDelete(): Promise<void> {
    if (confirm('Benutzer wirklich löschen?')) {
      try {
        await this.apiService.deleteUser(this.id);
        this.router.navigate(['/admin/user/list']);
      } catch (error) {
        console.error('Error deleting user', error);
      }
    }
  }

  onReset() {
    this.userForm.reset();
    if (this.user) {
      this.userForm.patchValue({
        name: this.user.name,
        password: '',
        password2: '',
        playerId: '' + this.user.playerId,
        selectedRoles: this.user.roles,
        notes: this.user.note,
      });
    }
  }
}
